import { CommonModule } from '@angular/common';
import { Component, HostBinding } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import {
  MmuCookiePreferencesService,
  MmuFaviconService,
  MmuFeatureFlagsService,
  MmuRemoteComponentConfig,
  MmuTitleService,
  MmuUserService,
  RemoteModuleLoadingError
} from '@move/core';
import { filter, map, Observable } from 'rxjs';
import { SharedModule } from './shared/shared.module';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styles: [
    `:host {
      display: block;
    }`
  ],
  standalone: true,
  imports: [
    CommonModule,
    SharedModule
  ]
})
export class AppComponent {
  @HostBinding('@.disabled')
  readonly animationsDisabled: boolean;
  readonly authenticated$: Observable<boolean>;
  readonly notificationConfiguration: MmuRemoteComponentConfig;

  constructor(
    private _userService: MmuUserService,
    private _ffService: MmuFeatureFlagsService,
    private _titleService: MmuTitleService,
    private _cookieSettings: MmuCookiePreferencesService,
    private _faviconService: MmuFaviconService,
    private _router: Router
  ) {
    // Detect if the website is tested by Selenium and disable animations for performance reasons
    this.animationsDisabled = Boolean(navigator.webdriver);
    this.authenticated$ = this._userService.authenticated$;
    this.notificationConfiguration = this._ffService.isFeatureEnabled('actionsNNG') ? {
      remoteModule: 'actions/NotificationCenterFragment',
      manifestUrl: '/actions/manifest.json',
      moduleFile: 'actions.js',
      moduleClass: 'NotificationCenterModule',
      componentClass: 'NotificationCenterComponent'
    } : {
      remoteModule: 'appliances/NotificationCenter',
      manifestUrl: '/manifest.json',
      moduleFile: 'appliances.js',
      moduleClass: 'NotificationCenterModule',
      componentClass: 'NotificationCenterComponent'
    };

    // Make sure the title adapts automatically to the current route
    this._titleService.subscribeToRouterEvents();

    // No idea what this does
    this._faviconService.init();

    this.requestCookiePreferences();
    this.redirectOnNavigationError();
  }

  private requestCookiePreferences(): void {
    /* It is a requirement to always show the legal texts to the customers, e.g. even if the cookie preferences have not
    * been set yet. On all other pages, the user must select his preferences first.
    * ToDo: When more verticals are migrated to the frontend-container, we need to ensure that the cookie settings are
    *  shown to the user when he navigates away from the legal pages using internal Angular routing navigation. */
    const pathRegex = new RegExp(/(user\/legal)/is);

    if (!pathRegex.test(window.location.pathname)) {
      this._cookieSettings.requestCookiePreferences();
    }
  }

  /**
   * Enable the redirection to the error page in case of a navigation errors caused by failed loading of remote modules.
   * @private
   */
  private redirectOnNavigationError(): void {
    this._router.events.pipe(
      filter((evt): evt is NavigationError => evt instanceof NavigationError),
      map(({ error }) => error),
      filter((error): error is RemoteModuleLoadingError => error instanceof RemoteModuleLoadingError)
    ).subscribe((error) => {
      this._router.navigate(['/error/navigation'], { skipLocationChange: true }).then();
    });
  }
}
